import {
  AuditOutlined,
  DashboardOutlined,
  DesktopOutlined,
  FileDoneOutlined,
  MenuFoldOutlined,
  MenuOutlined,
  MenuUnfoldOutlined,
  RollbackOutlined,
  ShopOutlined,
  SolutionOutlined,
  StarOutlined
} from '@ant-design/icons';
import { UserMenu } from '@hq/account';
import { truncateByWidth } from '@hq/utils';
import { Button, Drawer, Layout, Menu, Space, Tag, Typography } from 'antd';
import { useEffect, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useIsMobile } from '../components/useMobile';
import { appTitle, hq, useAuthStore } from '../session';
import { readOrgBrand } from '../org-brand';

const { Header, Sider, Content } = Layout;

/**
 * 供应商门户菜单（docs/05 §4.3）：按「日常作业 → 维护」排序。
 * 作业动线：概览（待办入口）→ 订单协同（待接单）→ 可供库存（外仓指令，出库确认）
 * → 对账开票（待确认）→ 退换货（订单后置异常处理）；商品备案与履约评价属维护/反馈。
 * 末尾三项为大屏：全屏展示（新窗口打开，不走账号登录态）+ 本主体设备 + 申请码确认。
 */
/** 新窗口打开的全屏大屏路由（凭屏端设备凭据独立取数）；非真实路径，不参与选中匹配。 */
const SCREEN_EXT_KEY = 'ext:/screen';

const MENU = [
  { key: '/dashboard', icon: <DashboardOutlined />, label: '企业概览' },
  { key: '/order', icon: <FileDoneOutlined />, label: '订单协同' },
  { key: '/settle', icon: <AuditOutlined />, label: '对账开票' },
  { key: '/return', icon: <RollbackOutlined />, label: '退换货' },
  { key: '/goods', icon: <ShopOutlined />, label: '商品备案' },
  { key: '/stock', icon: <SolutionOutlined />, label: '可供库存' },
  { key: '/evaluation', icon: <StarOutlined />, label: '履约评价' },
  { key: SCREEN_EXT_KEY, icon: <DesktopOutlined />, label: '打开大屏' },
  { key: '/screen-devices', icon: <DesktopOutlined />, label: '我的大屏' },
  { key: '/screen-scan', icon: <DesktopOutlined />, label: '申请码确认' }
];

export default function AppLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const profile = useAuthStore((state) => state.profile);
  const clear = useAuthStore((state) => state.clear);
  const patchProfile = useAuthStore((state) => state.patchProfile);
  // 侧栏受控折叠：底部用简化图标按钮替代默认整条 trigger
  const [collapsed, setCollapsed] = useState(false);
  // 移动端：<768px 侧栏改为抽屉，菜单选中后自动收起
  const isMobile = useIsMobile();
  const [drawerOpen, setDrawerOpen] = useState(false);

  // 登录时缓存的供应商名称（优先简称），避免头部直接展示雪花 orgId
  const brand = readOrgBrand();
  const orgName = brand?.orgShortName || brand?.orgName || null;

  // 外部入口项（ext:/screen）不参与路径匹配，避免把「打开大屏」当成当前页
  const selected =
    MENU.find((item) => location.pathname === item.key)?.key ??
    MENU.find((item) => !item.key.startsWith('ext:') && location.pathname.startsWith(`${item.key}/`))?.key ??
    '/dashboard';

  // 移动端切换菜单后自动收起抽屉
  useEffect(() => {
    if (isMobile) setDrawerOpen(false);
  }, [selected, isMobile]);

  // 侧栏内容（logo + 菜单）：桌面放 Sider，移动端放 Drawer，两端复用同一份
  const siderContent = (
    <>
      <div className="app-logo">
        <img className="app-logo__img" src="/logo.png" alt="logo" />
        {!collapsed && <span title={appTitle}>{truncateByWidth(appTitle)}</span>}
      </div>
      <div className="app-sider__menu">
        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={[selected]}
          items={MENU}
          onClick={({ key }) => (key === SCREEN_EXT_KEY ? window.open('/screen', '_blank') : navigate(key))}
        />
      </div>
    </>
  );

  return (
    <Layout style={{ minHeight: '100vh' }}>
      {isMobile ? (
        <Drawer
          placement="left"
          width={232}
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          closable={false}
          classNames={{ body: 'app-drawer__body' }}
          styles={{ body: { padding: 0, background: '#001529' } }}
        >
          {siderContent}
        </Drawer>
      ) : (
        <Sider
          className="app-sider"
          theme="dark"
          width={208}
          collapsible
          collapsed={collapsed}
          onCollapse={setCollapsed}
          trigger={null}
          style={{ position: 'sticky', top: 0, height: '100vh' }}
        >
          {siderContent}
        </Sider>
      )}
      <Layout>
        <Header
          style={{
            background: '#fff',
            paddingInline: isMobile ? 12 : 24,
            height: 56,
            // 不能用 56px 行高：antd ellipsis 单行文本是 inline-block + vertical-align: bottom，
            // 会被 56px 行盒压到最底部，姓名与「退出」就错行了；行高交回 normal 由 flex 居中
            lineHeight: 'normal',
            display: 'flex',
            flexWrap: 'nowrap',
            gap: 16,
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
        >
          <Space>
            {/* 收起/展开：置顶栏左侧，贴近导航栏；移动端改为抽屉开关 */}
            <Button
              type="text"
              style={{ marginInlineStart: -12 }}
              icon={isMobile ? <MenuOutlined /> : collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
              onClick={() => (isMobile ? setDrawerOpen(true) : setCollapsed(!collapsed))}
            />
            {!isMobile && <Typography.Text type="secondary">第三方 · 商品 / 订单 / 对账</Typography.Text>}
            {!isMobile && <Tag color="gold">SUPPLIER 独立安全域</Tag>}
          </Space>
          <Space size={isMobile ? 8 : 16} style={{ flexShrink: isMobile ? 1 : 0, minWidth: 0, whiteSpace: 'nowrap' }}>
            {profile ? (
              <UserMenu
                hq={hq}
                compact={isMobile}
                subtitle={orgName ?? undefined}
                profile={{
                  userId: profile.userId,
                  userCode: profile.userCode,
                  name: profile.name,
                  orgName: orgName,
                  deptName: profile.deptName,
                  mobile: profile.mobile,
                  email: profile.email,
                  avatarUrl: profile.avatarUrl,
                  level: profile.level,
                  levelText: profile.level
                }}
                onProfileUpdated={(next) =>
                  patchProfile({
                    avatarUrl: next.avatarUrl,
                    deptName: next.deptName,
                    mobile: next.mobile,
                    email: next.email,
                    userCode: next.userCode
                  })
                }
                onLogout={() => {
                  clear();
                  navigate('/login', { replace: true });
                }}
              />
            ) : (
              <Typography.Text type="secondary">未登录</Typography.Text>
            )}
          </Space>
        </Header>
        <Content style={{ padding: isMobile ? 12 : 24 }}>
          <Outlet />
        </Content>
      </Layout>
    </Layout>
  );
}
